import { Static, Type } from '@sinclair/typebox';
import { AFFILIATE_BOUNTY_MINOR } from './constants/affiliate_program';
import { PROJECT_CONFIG } from './project_branding/config';
import { NotificationType } from './translation_namespace/notification_namespace';
import { formatPriceFromMinorUnit } from './translation_namespace/utils';

// =============================================================================
// Affiliation v2 (cf. docs/to_implement/affiliation-v2-plan.md)
// Enums (mirror of Prisma enums in packages/backend/prisma-pg/schema.prisma)
// =============================================================================

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateRegime
export enum AffiliateRegime {
  // Affilié client Cfixé — cagnotte convertie en réduction sur son propre abo (code BUDGET_MINOR).
  CLIENT_PRO = 'CLIENT_PRO',
  // Affilié particulier — carte cadeau (différé, validation juridique préalable).
  PARTICULIER = 'PARTICULIER',
  // Affilié pro non-client — virement bancaire sur facture (différé, traitement manuel).
  PRO_EXTERNE = 'PRO_EXTERNE',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateStatus
export enum AffiliateStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateReferralReviewState
/**
 * Validation d'un PARRAINAGE (couple affilié ↔ filleul), et non du compte affilié.
 * C'est l'unité de contrôle anti-fraude : valider un compte une seule fois laisserait
 * passer tous les filleuls douteux ajoutés ensuite.
 */
export enum AffiliateReferralReviewState {
  /** Attribué, en attente de vérification : les commissions sont retenues. */
  PENDING = 'PENDING',
  /** Parrainage légitime : les commissions deviennent payables, y compris les suivantes. */
  APPROVED = 'APPROVED',
  /** Parrainage écarté : plus aucune commission n'est produite pour ce filleul. */
  REJECTED = 'REJECTED',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateCommissionStatus
export enum AffiliateCommissionStatus {
  /**
   * Gain enregistré mais PAS encore acquis : il attend la validation d'un admin.
   * C'est le garde-fou anti auto-parrainage : l'attribution du filleul, elle, se fait
   * toujours (on ne perd jamais un parrainage), seule la récompense est retenue.
   * Ne compte NI dans le solde à verser, NI dans le seuil de retrait.
   */
  REVIEW = 'REVIEW',
  PENDING = 'PENDING',
  PAID = 'PAID',
  VOID = 'VOID',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateCommissionKind
export enum AffiliateCommissionKind {
  COMMISSION = 'COMMISSION',
  OPENING_BALANCE = 'OPENING_BALANCE',
  ADJUSTMENT = 'ADJUSTMENT',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateReconciliationState
export enum AffiliateReconciliationState {
  PENDING = 'PENDING',
  ATTACHED = 'ATTACHED',
  IGNORED = 'IGNORED',
}

//⚠️IMPACT attention changer dans le fichier prisma l'enum AffiliateSource
/** Origine du compte affilié — sert au tri admin (les auto-inscriptions sont à modérer). */
export enum AffiliateSource {
  /** Créé à la main dans le back-office. */
  ADMIN = 'ADMIN',
  /** Auto-inscription depuis la landing publique du programme. */
  SELF_SIGNUP = 'SELF_SIGNUP',
  /** Activé par un pro client depuis « Mon compte » de l'app. */
  PRO_APP = 'PRO_APP',
  /** Repris de FirstPromoter par l'import one-shot (Lot 6). */
  IMPORT_FP = 'IMPORT_FP',
}

// =============================================================================
// Constantes économiques du programme (source de vérité partagée)
// =============================================================================

/**
 * Les valeurs vivent dans `constants/affiliate_program.ts` — un module SANS import,
 * consommable par les manifestes de features (le job CI `build-docs` exécute
 * `generate:user-docs` sans les dépendances de shared, typebox compris).
 * Ré-exportées ici pour que les consommateurs existants ne changent pas d'import.
 */
export {
  AFFILIATE_RATE_YEAR_1_PERCENT,
  AFFILIATE_RATE_YEAR_2_3_PERCENT,
  AFFILIATE_WINDOW_MONTHS,
  AFFILIATE_RATE_SWITCH_MONTH,
  AFFILIATE_BOUNTY_MINOR,
  AFFILIATE_PAYOUT_THRESHOLD_MINOR,
} from './constants/affiliate_program';

/**
 * Prime filleul formatée pour l'affichage (« 20,00 € »). Exception documentée à la règle « prix
 * formatés côté backend » : `AFFILIATE_BOUNTY_MINOR` est une constante marketing partagée, pas une
 * donnée utilisateur — même justification que la grille PLANS sur la page de choix d'abonnement.
 * Source unique pour la carte de parrainage, l'espace affilié et la page Parrainage.
 */
export const formatAffiliateBounty = (currency: string, locale: string): string =>
  formatPriceFromMinorUnit(AFFILIATE_BOUNTY_MINOR, currency, locale);

/**
 * Events du programme d'affiliation ÉLIGIBLES à une copie cachée ops.
 *
 * ⚠️ Éligible ne veut pas dire actif : la copie ne part que si l'environnement fournit
 * une adresse (`AFFILIATE_OPS_BCC_EMAIL`, vide par défaut — cf. `email.processor.ts`).
 * Ces emails sont nominatifs (nom, code de parrainage, montants, solde) ; les recopier
 * vers une boîte partagée est une décision d'exploitation, temporaire par nature, qui
 * doit s'activer et se couper sans redéploiement. L'adresse était auparavant en dur
 * ici : la copie partait donc en production sans qu'aucun réglage ne puisse l'arrêter.
 *
 * L'OTP de connexion est volontairement exclu de la liste : il transporte un code d'accès.
 */
export const AFFILIATE_OPS_BCC_EVENTS: ReadonlySet<NotificationType> = new Set([
  NotificationType.AFFILIATE_ACCOUNT_APPROVED,
  NotificationType.AFFILIATE_REFERRAL_SIGNUP,
  NotificationType.AFFILIATE_REFERRAL_SUBSCRIBED,
  NotificationType.AFFILIATE_COMMISSION_EARNED,
]);

/**
 * Les commissions d'un filleul non encore validé naissent en `REVIEW` : elles sont
 * enregistrées mais retenues. La validation se fait FILLEUL PAR FILLEUL
 * (`AffiliateReferralReviewState`), pas au niveau du compte affilié : un compte validé
 * une fois pourrait sinon apporter n'importe quoi ensuite sans nouveau contrôle.
 *
 * Passer à `false` ferait confiance au statut du compte affilié à la place ; à ne
 * faire que si la fraude par auto-parrainage cesse d'être un risque.
 */
export const AFFILIATE_REVIEW_PER_REFERRAL = true;
/** Fenêtre du cookie d'attribution `affiliate_ref` (jours). */
export const AFFILIATE_COOKIE_MAX_AGE_DAYS = 30;
/** Nom du query param public des liens affiliés. */
export const AFFILIATE_QUERY_PARAM = 'via';
/** Nom du cookie d'attribution posé côté LP / register (eTLD+1). */
export const AFFILIATE_REF_COOKIE = 'affiliate_ref';

/**
 * Casse canonique du `ref_token` dans les events de tracking (`affiliate_click`).
 *
 * Le token voyage dans l'URL (`?via=`) avec la casse que lui donne celui qui partage
 * ou recopie le lien, alors que l'attribution le résout en `mode: 'insensitive'` :
 * `?via=AbC` et `?via=abc` attribuent le MÊME affilié, mais produiraient deux valeurs
 * de propriété distinctes côté Plausible — dont le filtre `is` est sensible à la casse.
 * Les clics d'un même affilié se retrouveraient éparpillés sur plusieurs valeurs et le
 * compteur n'en verrait qu'une. On fige donc la prop en MAJUSCULES, la casse de
 * génération des tokens v2 (`generateAffiliateRefToken`) et celle du moteur promo
 * (`normalizeCode()`), pour que l'émission et la requête parlent de la même valeur.
 */
export function normalizeAffiliateRefTokenForTracking(refToken: string): string {
  return refToken.trim().toUpperCase();
}

/**
 * Valeurs de `ref_token` à interroger côté Stats API pour compter les clics.
 *
 * La forme normalisée suffit pour tout event émis depuis ce correctif. Les variantes
 * suivantes — casse telle qu'elle est stockée en base, puis minuscules — ne servent
 * qu'à ne pas perdre l'historique déjà enregistré avec la casse brute de l'URL
 * (les tokens repris de FirstPromoter ne sont pas tous en majuscules). Elles pourront
 * disparaître quand la fenêtre d'analyse ne remontera plus avant la MEP.
 */
export function affiliateRefTokenTrackingVariants(refToken: string): string[] {
  const raw = refToken.trim();
  return Array.from(new Set([normalizeAffiliateRefTokenForTracking(raw), raw, raw.toLowerCase()]));
}

// =============================================================================
// Points d'entrée publics du programme
// =============================================================================

/** Landing publique de présentation du programme (site marketing). */
export const AFFILIATE_LANDING_PATH = '/affiliation';
/** CGU du programme d'affiliation (site marketing) — référencées à l'inscription. */
export const AFFILIATE_TERMS_PATH = '/conditions-affiliation';
/** Espace affilié — connexion OTP (app pro). */
export const AFFILIATE_SPACE_LOGIN_PATH = '/affiliation/login';
/** Espace affilié — dashboard (app pro). */
export const AFFILIATE_SPACE_ACCOUNT_PATH = '/affiliation/account';

/** URL absolue de la landing du programme. */
export function affiliateLandingUrl(): string {
  return `${PROJECT_CONFIG.marketing_site_url}${AFFILIATE_LANDING_PATH}`;
}

/** URL absolue des CGU du programme — un seul endroit à changer si le document bouge. */
export function affiliateTermsUrl(): string {
  return `${PROJECT_CONFIG.marketing_site_url}${AFFILIATE_TERMS_PATH}`;
}

// =============================================================================
// Coordonnées de versement (régimes PARTICULIER / PRO_EXTERNE)
// =============================================================================

/**
 * Validation IBAN (ISO 13616) : format + clé de contrôle mod-97. Purement
 * structurelle — elle ne dit pas que le compte existe, seulement que la saisie
 * n'est pas une coquille. Utilisée côté formulaire ET côté backend.
 */
export function normalizeIban(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

export function isValidIban(value: string): boolean {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  // Les 4 premiers caractères passent à la fin, les lettres deviennent 10..35, mod 97 == 1.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const mapped = char >= 'A' && char <= 'Z' ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of mapped) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}

/** BIC/SWIFT (ISO 9362) : 8 ou 11 caractères. */
export function isValidBic(value: string): boolean {
  return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.replace(/\s/g, '').toUpperCase());
}

/** IBAN masqué pour l'affichage — seuls le pays et les 4 derniers caractères restent lisibles. */
export function maskIban(value: string): string {
  const iban = normalizeIban(value);
  if (iban.length < 8) return '••••';
  return `${iban.slice(0, 4)} •••• ${iban.slice(-4)}`;
}

/**
 * Valeurs de régime acceptées par les helpers. Union de chaînes plutôt que l'enum :
 * le backend manipule l'enum GÉNÉRÉ PAR PRISMA (nominalement distinct de celui-ci,
 * bien que jumeau) et doit pouvoir appeler ces fonctions sans cast.
 */
export type AffiliateRegimeValue = `${AffiliateRegime}`;

/**
 * Régimes qui exigent des coordonnées de versement avant toute demande de retrait.
 * CLIENT_PRO en est exempt : sa cagnotte est convertie en réduction sur son propre
 * abonnement (aucun mouvement d'argent sortant).
 */
export function affiliateRequiresPayoutDetails(regime: AffiliateRegimeValue): boolean {
  return regime === AffiliateRegime.PRO_EXTERNE || regime === AffiliateRegime.PARTICULIER;
}

/**
 * Saisie des coordonnées de versement par l'affilié lui-même (espace affilié).
 * Tous les champs sont optionnels ici : la complétude est jugée par régime côté
 * backend (`PRO_EXTERNE` → IBAN + titulaire ; `PARTICULIER` → email de livraison).
 * Une chaîne vide efface le champ.
 */
export const AffiliatePayoutDetailsBodySchema = Type.Object({
  /** Titulaire du compte — doit correspondre à l'identité facturée. */
  holder_name: Type.Optional(Type.String({ maxLength: 255 })),
  iban: Type.Optional(Type.String({ maxLength: 42 })),
  bic: Type.Optional(Type.String({ maxLength: 11 })),
  /** Raison sociale + SIRET : obligatoires pour facturer un virement à un pro. */
  company_name: Type.Optional(Type.String({ maxLength: 255 })),
  siret: Type.Optional(Type.String({ maxLength: 20 })),
  /** Adresse de livraison de la carte cadeau (régime PARTICULIER). */
  gift_card_email: Type.Optional(Type.String({ maxLength: 255 })),
});

/** Vue renvoyée à l'affilié : l'IBAN n'est JAMAIS re-servi en clair. */
export const AffiliatePayoutDetailsSchema = Type.Object({
  holder_name: Type.Optional(Type.String()),
  iban_masked: Type.Optional(Type.String()),
  bic: Type.Optional(Type.String()),
  company_name: Type.Optional(Type.String()),
  siret: Type.Optional(Type.String()),
  gift_card_email: Type.Optional(Type.String()),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});

/**
 * Complétude des coordonnées, par régime — même règle côté formulaire et côté API.
 * PRO_EXTERNE : virement sur facture → identité facturable (raison sociale + SIRET)
 * ET compte bancaire. PARTICULIER : carte cadeau → adresse de livraison.
 * CLIENT_PRO : rien (cagnotte convertie en réduction d'abonnement).
 */
export function isAffiliatePayoutDetailsComplete(
  regime: AffiliateRegimeValue,
  details: {
    holder_name?: string | null;
    iban?: string | null;
    company_name?: string | null;
    siret?: string | null;
    gift_card_email?: string | null;
  },
): boolean {
  const filled = (value?: string | null): boolean => typeof value === 'string' && value.trim().length > 0;
  if (regime === AffiliateRegime.PRO_EXTERNE) {
    return filled(details.iban) && filled(details.holder_name) && filled(details.company_name) && filled(details.siret);
  }
  if (regime === AffiliateRegime.PARTICULIER) return filled(details.gift_card_email);
  return true;
}

/** Réponse de mise à jour des coordonnées — évite de re-servir tout le dashboard. */
export const AffiliatePayoutDetailsResponseSchema = Type.Object({
  payout_details: Type.Optional(AffiliatePayoutDetailsSchema),
  payout_details_complete: Type.Boolean(),
});

/** Vue admin : IBAN en clair — c'est l'admin qui exécute le virement. */
export const AdminAffiliatePayoutDetailsSchema = Type.Object({
  holder_name: Type.Optional(Type.String()),
  iban: Type.Optional(Type.String()),
  bic: Type.Optional(Type.String()),
  company_name: Type.Optional(Type.String()),
  siret: Type.Optional(Type.String()),
  gift_card_email: Type.Optional(Type.String()),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});

// =============================================================================
// Admin — GET /app/admin/affiliate (liste)
// =============================================================================

export const AdminAffiliateListItemSchema = Type.Object({
  affiliate_id: Type.Number(),
  email: Type.String(),
  first_name: Type.Optional(Type.String()),
  last_name: Type.Optional(Type.String()),
  ref_token: Type.String(),
  regime: Type.Enum(AffiliateRegime),
  status: Type.Enum(AffiliateStatus),
  /** Origine du compte — une auto-inscription PENDING attend une validation admin. */
  source: Type.Enum(AffiliateSource),
  professional_id: Type.Optional(Type.Number()),
  bounty_code: Type.Optional(Type.String()),
  /** Nb de filleuls encore dans la fenêtre des 36 mois. */
  active_referred_count: Type.Number(),
  referred_count: Type.Number(),
  /** Solde dû (Σ lignes PENDING). */
  pending_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'pending_amount_minor' } }),
  /** Déjà versé (Σ lignes PAID). */
  paid_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'paid_amount_minor' } }),
  /** Gains retenus en attente de validation admin (Σ lignes REVIEW). */
  review_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'review_amount_minor' } }),
  /** Nb de lignes REVIEW — pilote le badge « à valider » de la liste. */
  review_count: Type.Number(),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at' } }),
  activated_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'activated_at' } })),
  /** Demande de versement en attente (espace affilié, seuil 50 €). */
  payout_requested_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'payout_requested_at' } }),
  ),
});

export const AdminAffiliateListResponseSchema = Type.Object({
  items: Type.Array(AdminAffiliateListItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

// =============================================================================
// Admin — GET /app/admin/affiliate/:id (fiche : échéancier filleuls + ledger)
// =============================================================================

export const AdminAffiliateReferralSchema = Type.Object({
  professional_id: Type.Number(),
  name: Type.String(),
  /** Date du 1er encaissement — départ de l'horloge 36 mois ; absent si pas encore payant. */
  referred_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'referred_at' } })),
  /** Mois de relation courant (1..n) ; absent si pas encore payant. */
  relation_month_index: Type.Optional(Type.Number()),
  /** Taux en vigueur aujourd'hui : 30, 10 ou 0. */
  current_rate_percent: Type.Number(),
  /** Date de bascule 30 % → 10 % (fin du mois 12). */
  rate_switch_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'rate_switch_at' } })),
  /** Fin de la fenêtre des 36 mois. */
  window_end_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'window_end_at' } })),
  total_commission_formatted: Type.String({ meta: { is_price: true, source_key: 'total_commission_minor' } }),
  /** Validation du parrainage lui-même. Absent pour un filleul antérieur au dispositif. */
  review_state: Type.Optional(Type.Enum(AffiliateReferralReviewState)),
  /** Gains de CE filleul actuellement retenus, en attente de la décision. */
  held_commission_formatted: Type.String({ meta: { is_price: true, source_key: 'held_commission_minor' } }),
});

export const AdminAffiliateCommissionSchema = Type.Object({
  affiliate_commission_id: Type.Number(),
  kind: Type.Enum(AffiliateCommissionKind),
  status: Type.Enum(AffiliateCommissionStatus),
  referred_professional_id: Type.Optional(Type.Number()),
  referred_professional_name: Type.Optional(Type.String()),
  pro_subscription_segment_id: Type.Optional(Type.Number()),
  external_ref: Type.Optional(Type.String()),
  amount_base_formatted: Type.String({ meta: { is_price: true, source_key: 'amount_minor_base' } }),
  amount_commission_formatted: Type.String({ meta: { is_price: true, source_key: 'amount_minor_commission' } }),
  /** Montant brut (centimes) — nécessaire au front pour totaliser une sélection de règlement. */
  amount_minor_commission_raw: Type.Number(),
  rate_percent: Type.Optional(Type.Number()),
  relation_month_index: Type.Optional(Type.Number()),
  notes_admin: Type.Optional(Type.String()),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  paid_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'paid_at', with_time: true } }),
  ),
});

export const AdminAffiliateDetailSchema = Type.Object({
  affiliate_id: Type.Number(),
  email: Type.String(),
  first_name: Type.Optional(Type.String()),
  last_name: Type.Optional(Type.String()),
  phone: Type.Optional(Type.String()),
  ref_token: Type.String(),
  /** Lien public copiable `https://propelsia.ai/?via=<ref_token>`. */
  referral_url: Type.String(),
  regime: Type.Enum(AffiliateRegime),
  status: Type.Enum(AffiliateStatus),
  source: Type.Enum(AffiliateSource),
  /** Acceptation des CGU du programme (auto-inscription / activation depuis l'app). */
  terms_accepted_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'terms_accepted_at', with_time: true } }),
  ),
  professional_id: Type.Optional(Type.Number()),
  professional_name: Type.Optional(Type.String()),
  firstpromoter_id: Type.Optional(Type.String()),
  bounty_code: Type.Optional(Type.String()),
  notes_admin: Type.Optional(Type.String()),
  /** Coordonnées de versement saisies par l'affilié (régimes PARTICULIER / PRO_EXTERNE). */
  payout_details: Type.Optional(AdminAffiliatePayoutDetailsSchema),
  pending_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'pending_amount_minor' } }),
  paid_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'paid_amount_minor' } }),
  /** Gains retenus en attente de validation admin (Σ lignes REVIEW). */
  review_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'review_amount_minor' } }),
  /** Nb de lignes REVIEW — pilote le bouton « valider les gains ». */
  review_count: Type.Number(),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at' } }),
  activated_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'activated_at' } })),
  /** Demande de versement en attente (espace affilié, seuil 50 €), remise à null au règlement. */
  payout_requested_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'payout_requested_at' } }),
  ),
  referrals: Type.Array(AdminAffiliateReferralSchema),
  ledger: Type.Array(AdminAffiliateCommissionSchema),
});

// =============================================================================
// Admin — GET /app/admin/affiliate/professionals/search (rattachement manuel)
// =============================================================================

/** Nombre max de pros renvoyés par la recherche de rattachement (liste de choix, pas un export). */
export const ADMIN_AFFILIATE_PROFESSIONAL_SEARCH_LIMIT = 20;

export const AdminAffiliateProfessionalSearchItemSchema = Type.Object({
  professional_id: Type.Number(),
  name: Type.String(),
  email: Type.Optional(Type.String()),
  /**
   * Affilié DÉJÀ rattaché à ce pro, le cas échéant. Renvoyé pour que l'admin voie le
   * conflit AVANT de cliquer : un pro ne peut être la cible de conversion cagnotte →
   * budget que d'un seul compte affilié.
   */
  linked_affiliate_id: Type.Optional(Type.Number()),
  linked_affiliate_email: Type.Optional(Type.String()),
});

export const AdminAffiliateProfessionalSearchResponseSchema = Type.Object({
  items: Type.Array(AdminAffiliateProfessionalSearchItemSchema),
});

/**
 * Recherche d'ambassadeurs (fiche pro admin → « Attribuer un parrain »). Même esprit que la
 * recherche de pros ci-dessus : une liste de choix, pas un export.
 */
export const AdminAffiliateSearchItemSchema = Type.Object({
  affiliate_id: Type.Number(),
  email: Type.String(),
  first_name: Type.Optional(Type.String()),
  last_name: Type.Optional(Type.String()),
  ref_token: Type.String(),
  status: Type.Enum(AffiliateStatus),
  regime: Type.Enum(AffiliateRegime),
});

export const AdminAffiliateSearchResponseSchema = Type.Object({
  items: Type.Array(AdminAffiliateSearchItemSchema),
});

/**
 * Rattachement manuel d'un FILLEUL à un ambassadeur (`professional.referred_by_affiliate_id`).
 * À ne pas confondre avec `AdminAffiliateLinkProfessionalBodySchema` (compte pro DE l'affilié).
 */
export const AdminAffiliateAttachReferralBodySchema = Type.Object({
  professional_id: Type.Number({ minimum: 1 }),
  /**
   * Rejouer les commissions des factures déjà payées par le pro (l'horloge des 36 mois
   * démarre alors à sa 1ʳᵉ facture). Sans l'option, elle démarre au prochain encaissement.
   */
  backfill_paid_invoices: Type.Optional(Type.Boolean()),
  /** Envoyer l'email « nouveau filleul » à l'ambassadeur (défaut : oui). */
  notify_affiliate: Type.Optional(Type.Boolean()),
  /** Note libre (source de la demande, contexte) — tracée à l'audit et sur les lignes rejouées. */
  note: Type.Optional(Type.String({ maxLength: 500 })),
});

/** Aperçu avant rattachement : conflit éventuel + ce que produirait le rattrapage rétroactif. */
export const AdminAffiliateAttachReferralPreviewSchema = Type.Object({
  /** Parrain déjà attribué au pro, le cas échéant (le rattachement sera refusé). */
  current_referrer: Type.Optional(
    Type.Object({
      affiliate_id: Type.Number(),
      email: Type.String(),
      first_name: Type.Optional(Type.String()),
      last_name: Type.Optional(Type.String()),
    }),
  ),
  /** Le pro est l'établissement de l'ambassadeur ou partage un email avec lui : refusé. */
  is_self_referral: Type.Boolean(),
  /** Factures payées qui produiraient une commission si l'option rétroactive est cochée. */
  backfill_invoices_count: Type.Number(),
  /** Factures payées sans segment résoluble (à saisir à la main). */
  backfill_skipped_count: Type.Number(),
  backfill_total_formatted: Type.String({ meta: { is_price: true, source_key: 'backfill_total_minor' } }),
  /** Date de 1ʳᵉ facture retenue = `referred_at` qui serait posé avec l'option rétroactive. */
  backfill_referred_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'backfill_referred_at' } }),
  ),
});

/** Rattachement (ou détachement, `professional_id: null`) manuel d'un compte pro à un affilié. */
export const AdminAffiliateLinkProfessionalBodySchema = Type.Object({
  professional_id: Type.Union([Type.Number({ minimum: 1 }), Type.Null()]),
  /**
   * Régime de repli, EXIGÉ au seul détachement d'un affilié `CLIENT_PRO` : sans pro
   * rattaché, ce régime n'a plus d'abonnement à créditer (règlement impossible, aucune
   * coordonnée de versement réclamée). Le rattachement, lui, impose `CLIENT_PRO` et
   * ignore ce champ — on ne devine jamais un régime, on le fait choisir.
   */
  regime: Type.Optional(Type.Enum(AffiliateRegime)),
});

// =============================================================================
// Admin — GET /app/admin/affiliate/:id/stats (funnel Plausible + DB)
// =============================================================================

export const AdminAffiliateStatsTimeseriesPointSchema = Type.Object({
  date: Type.String(),
  clicks: Type.Number(),
});

export const AdminAffiliateStatsSourceSchema = Type.Object({
  source: Type.String(),
  visits: Type.Number(),
});

export const AdminAffiliateStatsResponseSchema = Type.Object({
  /** Clics (Plausible `affiliate_click`) — trafic amont uniquement, jamais une base de calcul. */
  clicks_total: Type.Optional(Type.Number()),
  clicks_timeseries: Type.Array(AdminAffiliateStatsTimeseriesPointSchema),
  top_sources: Type.Array(AdminAffiliateStatsSourceSchema),
  /** Inscriptions attribuées (DB : professional.referred_by_affiliate_id). */
  signups_count: Type.Number(),
  /** Filleuls payants (DB : referred_at != null). */
  paying_count: Type.Number(),
  /** Commissions (ledger) : nb de lignes COMMISSION. */
  commissions_count: Type.Number(),
});

// =============================================================================
// Admin — mutations
// =============================================================================

export const AdminAffiliateCreateBodySchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
  first_name: Type.Optional(Type.String({ maxLength: 255 })),
  last_name: Type.Optional(Type.String({ maxLength: 255 })),
  phone: Type.Optional(Type.String({ maxLength: 64 })),
  regime: Type.Enum(AffiliateRegime),
  professional_id: Type.Optional(Type.Number({ minimum: 1 })),
  /** Optionnel : auto-généré depuis l'email sinon. */
  ref_token: Type.Optional(Type.String({ minLength: 3, maxLength: 64, pattern: '^[a-zA-Z0-9_-]+$' })),
  notes_admin: Type.Optional(Type.String({ maxLength: 5000 })),
});

export const AdminAffiliateUpdateBodySchema = Type.Object({
  first_name: Type.Optional(Type.String({ maxLength: 255 })),
  last_name: Type.Optional(Type.String({ maxLength: 255 })),
  phone: Type.Optional(Type.String({ maxLength: 64 })),
  regime: Type.Optional(Type.Enum(AffiliateRegime)),
  status: Type.Optional(Type.Enum(AffiliateStatus)),
  /**
   * Absent = rattachement inchangé ; `null` = détacher le pro (conservé par le pipe de
   * validation grâce à `meta.keep_null`). Le détachement suit la même règle que
   * `POST /:id/professional` : un CLIENT_PRO doit repartir avec un autre `regime`.
   */
  professional_id: Type.Optional(Type.Union([Type.Number({ minimum: 1 }), Type.Null()], { meta: { keep_null: true } })),
  notes_admin: Type.Optional(Type.String({ maxLength: 5000 })),
});

export const AdminAffiliateManualCommissionBodySchema = Type.Object({
  kind: Type.Union([
    Type.Literal(AffiliateCommissionKind.COMMISSION),
    Type.Literal(AffiliateCommissionKind.ADJUSTMENT),
  ]),
  /** Centimes — négatif autorisé pour un ADJUSTMENT (clawback manuel). */
  amount_minor_commission: Type.Number({ minimum: -1000000, maximum: 1000000 }),
  referred_professional_id: Type.Optional(Type.Number({ minimum: 1 })),
  /** Notes obligatoires : c'est la trace du passage manuel intérimaire. */
  notes_admin: Type.String({ minLength: 3, maxLength: 5000 }),
});

/**
 * Validation (ou refus) d'un PARRAINAGE. C'est l'action principale de modération :
 * on juge un filleul, pas un compte affilié — un compte validé une fois pourrait
 * sinon apporter n'importe quoi ensuite. La décision vaut pour les gains déjà retenus
 * de ce filleul ET pour tous ceux qu'il produira sur ses 36 mois.
 */
export const AdminAffiliateReviewReferralBodySchema = Type.Object({
  /** Filleul jugé (`professional_id`). */
  referred_professional_id: Type.Number({ minimum: 1 }),
  decision: Type.Union([Type.Literal('APPROVE'), Type.Literal('REJECT')]),
  /** Motif, obligatoire pour un refus. Tracé dans le ledger et dans l'audit. */
  reason: Type.Optional(Type.String({ maxLength: 500 })),
});

/**
 * Validation (ou refus) de lignes de gains isolées : `REVIEW` → `PENDING` (payable)
 * ou `VOID` (refusé). Filet pour les lignes sans filleul (solde d'ouverture,
 * ajustement manuel) ; la modération courante passe par le parrainage ci-dessus.
 */
export const AdminAffiliateReviewCommissionsBodySchema = Type.Object({
  decision: Type.Union([Type.Literal('APPROVE'), Type.Literal('REJECT')]),
  /** Lignes visées ; absent = toutes les lignes en attente de validation de l'affilié. */
  commission_ids: Type.Optional(Type.Array(Type.Number({ minimum: 1 }), { maxItems: 500 })),
  /** Motif, obligatoire pour un refus. Tracé dans `notes_admin` et dans l'audit. */
  reason: Type.Optional(Type.String({ maxLength: 500 })),
});

export const AdminAffiliateReviewCommissionsResponseSchema = Type.Object({
  /** Nombre de lignes effectivement basculées. */
  affected_count: Type.Number(),
  total_formatted: Type.String({ meta: { is_price: true, source_key: 'total_minor' } }),
});

/**
 * Rapatriement FirstPromoter (cohabitation v1/v2). `apply: false` = simulation :
 * l'admin voit ce qui serait écrit avant de lancer un vrai run.
 */
export const AdminAffiliateFirstpromoterSyncBodySchema = Type.Object({
  apply: Type.Boolean(),
});

export const AdminAffiliateFirstpromoterSyncResponseSchema = Type.Object({
  /** false = clés FirstPromoter absentes : rien n'a été tenté. */
  configured: Type.Boolean(),
  planned_only: Type.Boolean(),
  affiliates_upserted: Type.Number(),
  /** Codes promo bounty 20 € créés pour des affiliés FP qui n'en avaient pas. */
  bounty_codes_created: Type.Number(),
  /** Créations de code bounty en échec : leurs filleuls tombent en CODE_NOT_FOUND. */
  bounty_codes_failed: Type.Number(),
  /** Affiliés FP reclassés PRO_EXTERNE → CLIENT_PRO (compte pro v2 retrouvé). */
  regimes_reclassified: Type.Number(),
  promoters_skipped_no_ref_token: Type.Number(),
  promoters_skipped_ref_token_conflict: Type.Number(),
  referrals_seen: Type.Number(),
  /** Parrainages rattachés à un pro migré qui n'en avait pas. */
  referrals_attached: Type.Number(),
  referrals_already_attributed: Type.Number(),
  /** FP et la base ne désignent pas le même parrain : arbitrage humain requis. */
  referrals_conflict: Type.Number(),
  /** Horloge 36 mois recalée sur la date réelle de 1er paiement v1. */
  referred_at_backfilled: Type.Number(),
  commissions_seen: Type.Number(),
  commissions_created: Type.Number(),
  commissions_status_updated: Type.Number(),
  skipped_non_cash: Type.Number(),
  skipped_unknown_promoter: Type.Number(),
  /** Lignes ignorées car le filleul est déjà migré : notre moteur les produit. */
  skipped_after_migration: Type.Number(),
  /** Filleul migré mais sans parrainage rattaché : ligne retenue au lieu d'être perdue. */
  held_migration_gap: Type.Number(),
  referrals_unresolved: Type.Number(),
  /** Lignes déjà importées, orphelines de filleul, rattachées rétroactivement. */
  commissions_relinked: Type.Number(),
  flagged_for_review: Type.Number(),
});

export const AdminAffiliateSettleBodySchema = Type.Object({
  commission_ids: Type.Array(Type.Number({ minimum: 1 }), { minItems: 1, maxItems: 500 }),
  /**
   * BUDGET_CONVERSION : régime CLIENT_PRO — convertit le total sélectionné en code
   * `subscription_promo_code` kind=BUDGET_MINOR sur l'abo du pro de l'affilié
   * (plafonné au coût annuel restant, le surplus reste PENDING).
   * BANK_TRANSFER : régime PRO_EXTERNE — virement exécuté sur les coordonnées de
   * l'affilié ; `payout_reference` porte la référence du virement.
   * GIFT_CARD : régime PARTICULIER — carte cadeau envoyée ; `payout_reference`
   * porte le numéro de commande / de carte.
   * MARK_PAID : marquage nu, sans canal (rattrapage / règlement historique).
   */
  mode: Type.Union([
    Type.Literal('BUDGET_CONVERSION'),
    Type.Literal('BANK_TRANSFER'),
    Type.Literal('GIFT_CARD'),
    Type.Literal('MARK_PAID'),
  ]),
  /** Référence du versement (virement / carte cadeau) — tracée dans le ledger et l'audit. */
  payout_reference: Type.Optional(Type.String({ maxLength: 255 })),
  notes_admin: Type.Optional(Type.String({ maxLength: 5000 })),
});

export const AdminAffiliateSettleResponseSchema = Type.Object({
  settled_count: Type.Number(),
  settled_total_formatted: Type.String({ meta: { is_price: true, source_key: 'settled_total_minor' } }),
  /** Code BUDGET_MINOR émis (mode BUDGET_CONVERSION). */
  promo_code: Type.Optional(Type.String()),
  /** Part non convertie (plafond coût annuel atteint) — restée PENDING. */
  surplus_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'surplus_minor' } })),
});

export const AdminAffiliateActionResponseSchema = Type.Object({
  success: Type.Boolean(),
});

// =============================================================================
// Admin — réconciliation import FP
// =============================================================================

export const AdminAffiliateReconciliationItemSchema = Type.Object({
  affiliate_import_reconciliation_id: Type.Number(),
  affiliate_id: Type.Optional(Type.Number()),
  affiliate_email: Type.Optional(Type.String()),
  fp_referral_id: Type.String(),
  email: Type.Optional(Type.String()),
  fp_first_payment_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'fp_first_payment_at' } }),
  ),
  state: Type.Enum(AffiliateReconciliationState),
  resolved_professional_id: Type.Optional(Type.Number()),
});

export const AdminAffiliateReconciliationListResponseSchema = Type.Object({
  items: Type.Array(AdminAffiliateReconciliationItemSchema),
  total: Type.Number(),
});

export const AdminAffiliateReconciliationResolveBodySchema = Type.Object({
  /** ATTACHED : rattacher au pro donné ; IGNORED : écarter définitivement. */
  state: Type.Union([
    Type.Literal(AffiliateReconciliationState.ATTACHED),
    Type.Literal(AffiliateReconciliationState.IGNORED),
  ]),
  professional_id: Type.Optional(Type.Number({ minimum: 1 })),
});

// =============================================================================
// Espace affilié self-service (Lot 5) — auth OTP + dashboard
// =============================================================================

/** Durée de validité d'un code OTP de connexion (minutes). */
export const AFFILIATE_OTP_TTL_MINUTES = 10;
/** Nombre d'essais max par code OTP (anti-bruteforce). */
export const AFFILIATE_OTP_MAX_ATTEMPTS = 5;
/** Durée de vie du JWT affilié (jours) — pas de refresh token au MVP. */
export const AFFILIATE_JWT_TTL_DAYS = 7;

export const AffiliateOtpRequestBodySchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
});

export const AffiliateOtpVerifyBodySchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
  code: Type.String({ pattern: '^[0-9]{6}$' }),
});

/** Réponse volontairement identique que l'email existe ou non (anti-énumération). */
export const AffiliateOtpRequestResponseSchema = Type.Object({
  success: Type.Boolean(),
});

/**
 * Auto-inscription au programme depuis la landing publique (parité v1 `register`).
 * Le compte est créé en PENDING : il attend une validation admin avant que son lien
 * `?via=` n'attribue quoi que ce soit. `regime` est déclaratif ; un email qui
 * correspond à un pro client est requalifié CLIENT_PRO côté backend.
 */
export const AffiliateSignupBodySchema = Type.Object({
  email: Type.String({ format: 'email', maxLength: 255 }),
  first_name: Type.String({ minLength: 1, maxLength: 255 }),
  last_name: Type.String({ minLength: 1, maxLength: 255 }),
  phone: Type.String({ minLength: 5, maxLength: 64, meta: { input_type: 'phone_number' } }),
  regime: Type.Union([Type.Literal(AffiliateRegime.PARTICULIER), Type.Literal(AffiliateRegime.PRO_EXTERNE)]),
  /** Acceptation des CGU du programme — horodatée en base. */
  accept_terms: Type.Boolean(),
});

/**
 * Réponse d'inscription : identique qu'un compte ait été créé ou que l'email soit
 * déjà connu (anti-énumération). Dans les deux cas un code OTP part par email, donc
 * le front enchaîne toujours sur l'écran « saisissez votre code ».
 */
export const AffiliateSignupResponseSchema = Type.Object({
  success: Type.Boolean(),
});

/**
 * Activation du programme depuis une session pro authentifiée (parité v1
 * « Activer mon compte ambassadeur »). Pas d'OTP : l'email du compte est déjà vérifié.
 */
export const AffiliateProActivateBodySchema = Type.Object({
  accept_terms: Type.Boolean(),
});

export const AffiliateLoginResponseSchema = Type.Object({
  token: Type.String(),
});

export const AffiliateSpaceReferralSchema = Type.Object({
  /** Nom commercial du filleul (l'affilié le connaît : c'est lui qui l'a parrainé). */
  name: Type.String(),
  referred_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'referred_at' } })),
  relation_month_index: Type.Optional(Type.Number()),
  current_rate_percent: Type.Number(),
  total_commission_formatted: Type.String({ meta: { is_price: true, source_key: 'total_commission_minor' } }),
  /** Où en est la vérification de ce parrainage (transparence côté affilié). */
  review_state: Type.Optional(Type.Enum(AffiliateReferralReviewState)),
});

export const AffiliateSpaceLedgerItemSchema = Type.Object({
  kind: Type.Enum(AffiliateCommissionKind),
  status: Type.Enum(AffiliateCommissionStatus),
  referred_professional_name: Type.Optional(Type.String()),
  amount_commission_formatted: Type.String({ meta: { is_price: true, source_key: 'amount_minor_commission' } }),
  rate_percent: Type.Optional(Type.Number()),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at' } }),
});

export const AffiliateSpaceMeSchema = Type.Object({
  email: Type.String(),
  first_name: Type.Optional(Type.String()),
  ref_token: Type.String(),
  referral_url: Type.String(),
  /** PENDING = compte créé, pas encore validé. Le lien attribue quand même : seuls les gains sont retenus. */
  status: Type.Enum(AffiliateStatus),
  regime: Type.Enum(AffiliateRegime),
  bounty_code: Type.Optional(Type.String()),
  pending_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'pending_amount_minor' } }),
  /** Gains enregistrés mais retenus tant qu'un admin ne les a pas validés. */
  review_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'review_amount_minor' } }),
  /**
   * true dès qu'au moins un centime est retenu. Indispensable côté front : les montants
   * sortent formatés (« 12,00 € »), donc incomparables à 0, et le `ledger` est tronqué —
   * en déduire la retenue depuis les lignes visibles masquerait la carte « gains retenus »
   * à tout affilié dont les lignes REVIEW sont sorties de la fenêtre d'affichage.
   */
  has_review_gains: Type.Boolean(),
  paid_amount_formatted: Type.String({ meta: { is_price: true, source_key: 'paid_amount_minor' } }),
  payout_threshold_formatted: Type.String({ meta: { is_price: true, source_key: 'payout_threshold_minor' } }),
  /** Σ PENDING ≥ 50 € → le bouton « demander un versement » s'active. */
  payout_threshold_reached: Type.Boolean(),
  payout_requested_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'payout_requested_at' } }),
  ),
  /** Clics 30 j (Plausible, uniquement le ref_token de l'affilié) — absent si indisponible. */
  clicks_30d: Type.Optional(Type.Number()),
  /** true si le régime impose des coordonnées de versement (tout sauf CLIENT_PRO). */
  payout_details_required: Type.Boolean(),
  /** true si les coordonnées attendues pour le régime sont complètes. */
  payout_details_complete: Type.Boolean(),
  payout_details: Type.Optional(AffiliatePayoutDetailsSchema),
  referrals: Type.Array(AffiliateSpaceReferralSchema),
  ledger: Type.Array(AffiliateSpaceLedgerItemSchema),
});

export const AffiliateSpaceActionResponseSchema = Type.Object({
  success: Type.Boolean(),
});

// =============================================================================
// Static types
// =============================================================================

export type AdminAffiliateListItem = Static<typeof AdminAffiliateListItemSchema>;
export type AdminAffiliateListResponse = Static<typeof AdminAffiliateListResponseSchema>;
export type AdminAffiliateReferral = Static<typeof AdminAffiliateReferralSchema>;
export type AdminAffiliateCommission = Static<typeof AdminAffiliateCommissionSchema>;
export type AdminAffiliateDetail = Static<typeof AdminAffiliateDetailSchema>;
export type AdminAffiliateStatsResponse = Static<typeof AdminAffiliateStatsResponseSchema>;
export type AdminAffiliateCreateBody = Static<typeof AdminAffiliateCreateBodySchema>;
export type AdminAffiliateUpdateBody = Static<typeof AdminAffiliateUpdateBodySchema>;
export type AdminAffiliateManualCommissionBody = Static<typeof AdminAffiliateManualCommissionBodySchema>;
export type AdminAffiliateReviewReferralBody = Static<typeof AdminAffiliateReviewReferralBodySchema>;
export type AdminAffiliateReviewCommissionsBody = Static<typeof AdminAffiliateReviewCommissionsBodySchema>;
export type AdminAffiliateReviewCommissionsResponse = Static<typeof AdminAffiliateReviewCommissionsResponseSchema>;
export type AdminAffiliateFirstpromoterSyncBody = Static<typeof AdminAffiliateFirstpromoterSyncBodySchema>;
export type AdminAffiliateFirstpromoterSyncResponse = Static<typeof AdminAffiliateFirstpromoterSyncResponseSchema>;
export type AdminAffiliateSettleBody = Static<typeof AdminAffiliateSettleBodySchema>;
export type AdminAffiliateSettleResponse = Static<typeof AdminAffiliateSettleResponseSchema>;
export type AdminAffiliateProfessionalSearchItem = Static<typeof AdminAffiliateProfessionalSearchItemSchema>;
export type AdminAffiliateProfessionalSearchResponse = Static<typeof AdminAffiliateProfessionalSearchResponseSchema>;
export type AdminAffiliateLinkProfessionalBody = Static<typeof AdminAffiliateLinkProfessionalBodySchema>;
export type AdminAffiliateSearchItem = Static<typeof AdminAffiliateSearchItemSchema>;
export type AdminAffiliateSearchResponse = Static<typeof AdminAffiliateSearchResponseSchema>;
export type AdminAffiliateAttachReferralBody = Static<typeof AdminAffiliateAttachReferralBodySchema>;
export type AdminAffiliateAttachReferralPreview = Static<typeof AdminAffiliateAttachReferralPreviewSchema>;
export type AdminAffiliateActionResponse = Static<typeof AdminAffiliateActionResponseSchema>;
export type AdminAffiliateReconciliationItem = Static<typeof AdminAffiliateReconciliationItemSchema>;
export type AdminAffiliateReconciliationListResponse = Static<typeof AdminAffiliateReconciliationListResponseSchema>;
export type AdminAffiliateReconciliationResolveBody = Static<typeof AdminAffiliateReconciliationResolveBodySchema>;
export type AffiliatePayoutDetailsBody = Static<typeof AffiliatePayoutDetailsBodySchema>;
export type AffiliatePayoutDetails = Static<typeof AffiliatePayoutDetailsSchema>;
export type AffiliatePayoutDetailsResponse = Static<typeof AffiliatePayoutDetailsResponseSchema>;
export type AdminAffiliatePayoutDetails = Static<typeof AdminAffiliatePayoutDetailsSchema>;
export type AffiliateSignupBody = Static<typeof AffiliateSignupBodySchema>;
export type AffiliateSignupResponse = Static<typeof AffiliateSignupResponseSchema>;
export type AffiliateProActivateBody = Static<typeof AffiliateProActivateBodySchema>;
export type AffiliateOtpRequestBody = Static<typeof AffiliateOtpRequestBodySchema>;
export type AffiliateOtpVerifyBody = Static<typeof AffiliateOtpVerifyBodySchema>;
export type AffiliateOtpRequestResponse = Static<typeof AffiliateOtpRequestResponseSchema>;
export type AffiliateLoginResponse = Static<typeof AffiliateLoginResponseSchema>;
export type AffiliateSpaceReferral = Static<typeof AffiliateSpaceReferralSchema>;
export type AffiliateSpaceLedgerItem = Static<typeof AffiliateSpaceLedgerItemSchema>;
export type AffiliateSpaceMe = Static<typeof AffiliateSpaceMeSchema>;
export type AffiliateSpaceActionResponse = Static<typeof AffiliateSpaceActionResponseSchema>;
